import { useFeedHistory, useNowPlaying } from "../../api/hooks.js";
import { Panel } from "../../design-system/surfaces.js";
import { formatClock, formatDuration } from "../../lib/format.js";
import { Artwork } from "../../player/artwork.js";

/**
 * What a feed has actually played, most recent first — the question a radio
 * listener asks most often and the one a live stream cannot answer by itself. It is
 * read when that feed changes track and at no other time, because that is when it
 * changes.
 */
export function RecentlyPlayed({
  feedId,
  heading,
}: {
  feedId: string;
  heading: string;
}) {
  const live = useNowPlaying(feedId);
  const airing = live.data?.nowPlaying ?? null;
  const history = useFeedHistory(
    feedId,
    airing?.historyId ?? null,
    feedId !== "",
  );
  const recent = (history.data ?? []).filter(
    (item) => item.historyId !== airing?.historyId,
  );

  return (
    <section className="max-w-5xl">
      <h2 className="font-mono text-[0.625rem] tracking-[0.14em] text-ink-3 uppercase">
        {heading}
      </h2>
      {history.loading && recent.length === 0 ? (
        <p className="mt-3 text-sm text-ink-3">Reading this feed's history…</p>
      ) : history.error && recent.length === 0 ? (
        <p className="mt-3 text-sm text-ink-3">
          This feed's history could not be read.
        </p>
      ) : recent.length === 0 ? (
        <p className="mt-3 text-sm text-ink-3">
          Nothing has aired on this feed yet.
        </p>
      ) : (
        <Panel className="mt-3 divide-y divide-border">
          {recent.map((item) => (
            <div
              key={item.historyId}
              className="flex items-center gap-3 px-3 py-2.5 sm:px-4"
            >
              <Artwork airing={item} className="size-10" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-ink">{item.title}</div>
                {item.artist ? (
                  <div className="truncate text-xs text-ink-3">
                    {item.artist}
                  </div>
                ) : null}
              </div>
              <span className="shrink-0 font-mono text-[0.6875rem] text-ink-3 tabular-nums">
                {formatDuration(item.durationMs)}
              </span>
              <span className="hidden w-16 shrink-0 text-right font-mono text-[0.6875rem] text-ink-3 tabular-nums sm:block">
                {formatClock(item.startedAt)}
              </span>
            </div>
          ))}
        </Panel>
      )}
    </section>
  );
}
